import { deprecationWarning } from "../../utils/deprecation"
import { isFiniteNumber } from "../utils/isFiniteNumber"

/** @public */
export type PropertyControls<ComponentProps = any, ArrayTypes = any> = {
    [K in keyof ComponentProps]?: ControlDescription<Partial<ComponentProps>>
}

type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>
export type CompactControlsDescription<P = any> = NumberControlDescription<P> | EnumControlDescription<P>

export type FlatControlDescription<P = any> =
    | Omit<NumberControlDescription<P>, "hidden">
    | Omit<EnumControlDescription<P>, "hidden">
    | Omit<BooleanControlDescription<P>, "hidden">
    | Omit<StringControlDescription<P>, "hidden">
    | Omit<ColorControlDescription<P>, "hidden">
    | Omit<FusedNumberControlDescription<P>, "hidden">
    | Omit<SegmentedEnumControlDescription<P>, "hidden">
    | Omit<ImageControlDescription<P>, "hidden">
    | Omit<FileControlDescription<P>, "hidden">
    | Omit<ComponentInstanceDescription<P>, "hidden">

/** @public */
export type ControlDescription<P = any> =
    | NumberControlDescription<P>
    | EnumControlDescription<P>
    | BooleanControlDescription<P>
    | StringControlDescription<P>
    | ColorControlDescription<P>
    | FusedNumberControlDescription<P>
    | SegmentedEnumControlDescription<P>
    | ImageControlDescription<P>
    | FileControlDescription<P>
    | ComponentInstanceDescription<P>
    | ArrayControlDescription<P>

/**
 * Used by the {@link PropertyControls} and specifies the type of user interface for receiving
 * input. Each field has a distinct set of properties though they all accept `title` and `hidden`
 * properties.
 *
 * @remarks
 * ```javascript
 * addPropertyControls(MyComponent, {
 *   text: {
 *     type: ControlType.String,
 *     title: "Text",
 *     hidden: (props) => true
 *   },
 * }
 * ```
 * @public
 */
export const enum ControlType {
    /**
     * A property control that displays an on / off checkbox. The associated property will be `true` or `false`,
     * depending on the state of the checkbox. Includes an optional `defaultValue`, which is set to `true` by default. You can also customize the labels displayed in the property panel with the `enabledTitle` and `disabledTitle` properties.
     *
     * @remarks
     * ```javascript
     * addPropertyControls(MyComponent, {
     *   isCentered: {
     *     type: ControlType.Boolean,
     *     title: "Center",
     *     defaultValue: true,
     *     enabledTitle: "On",
     *     disabledTitle: "Off",
     *   },
     * })
     * ```
     */
    Boolean = "boolean",
    /**
     * A property control that accepts any numeric value. This will be provided directly as a property.
     * Will display an input field with a range slider by default. The
     * `displayStepper` option can be provided to include a stepper control instead.
     *
     * @remarks
     * ```javascript
     * addPropertyControls(MyComponent, {
     *   offset: {
     *     type: ControlType.Number,
     *     defaultValue: 5,
     *     min: 0,
     *     max: 10,
     *     unit: "px",
     *     step: 0.1,
     *     displayStepper: true,
     *   },
     * })
     * ```
     */
    Number = "number",
    /**
     * A property control that accepts plain text values. This will be provided directly as a property.
     * Will display an input field with an optional placeholder value.
     *
     * @remarks
     * ```javascript
     * addPropertyControls(MyComponent, {
     *   offset: {
     *     type: ControlType.String,
     *     defaultValue: "Framer X",
     *     placeholder: "Type something…",
     *   },
     * }
     * ```
     */
    String = "string",
    /**
     * A property control that can be used to take a single number or four distinct numeric input fields.
     * The typical use case for this control is for visual properties like border, padding or margin.
     * It will display an input field to accept a single value, alongside a segmented control
     * allowing four distinct values to be provided.
     *
     * ```javascript
     * addPropertyControls(MyComponent, {
     *   title: {
     *     type: ControlType.FusedNumber,
     *     defaultValue: 0,
     *     toggleKey: "isPerSide",
     *     valueKeys: ["top", "left", "right", "bottom"],
     *     valueLabels: ["T", "L", "R", "B"],
     *     min: 0,
     *   },
     * })
     * ```
     */
    FusedNumber = "fusednumber",
    /**
     * A property control that represents a list of options. The selected option will be provided as a property.
     * This control is displayed as a dropdown menu in which a user can select one of the items.
     *
     * ```javascript
     * addPropertyControls(MyComponent, {
     *   value: {
     *     type: ControlType.Enum,
     *     defaultValue: "a",
     *     options: ["a", "b", "c"],
     *     optionTitles: ["Option A", "Option B", "Option C"],
     *   },
     * })
     * ```
     */
    Enum = "enum",
    /**
     * A property control that represents a list of option. The selected option will be provided as a property.
     * This control is displayed as a segmented control. Otherwise, it behaves exactly the
     * same as {@link ControlType.Enum}.
     *
     * @remarks
     * ```javascript
     * addPropertyControls(MyComponent, {
     *   value: {
     *     type: ControlType.SegmentedEnum,
     *     defaultValue: "a",
     *     options: ["a", "b", "c"],
     *     optionTitles: ["Option A", "Option B", "Option C"],
     *   },
     * })
     * ```
     */
    SegmentedEnum = "segmentedenum",
    /**
     * A property control that represents a color value. It will be provided as a string via properties.
     * This control is displayed as a color field and will provide the selected color in either
     * HEX (`"#fff"`) or HSL (`hsla(203, 87%, 50%, 0.5)`) notation, depending on
     * whether there is an alpha channel.
     *
     * @remarks
     * ```javascript
     * addPropertyControls(MyComponent, {
     *   background: {
     *     type: ControlType.Color,
     *     defaultValue: "#fff",
     *   },
     * })
     * ```
     */
    Color = "color",
    /**
     * A property control that represents an image resource. It will be provided as an URL via properties.
     * Displayed as an image picker with associated file picker. The chosen asset
     * will be provided as a fully qualified URL.
     *
     * @remarks
     * ```jsx
     * addPropertyControls(MyComponent, {
     *   avatar: {
     *     type: ControlType.Image,
     *   }
     * })
     * ```
     */
    Image = "image",
    /**
     * A property control that represents a resource. It will be provided as an URL via properties.
     * Displayed as an file picker that will open a native file browser. The
     * selected file will be provided as a fully qualified URL. The
     * `allowedFileTypes` property must be provided to specify acceptable file
     * types.
     *
     * @remarks
     * ```javascript
     * addPropertyControls(MyComponent, {
     *   asset: {
     *     type: ControlType.File,
     *     allowedFileTypes: ["pdf"],
     *   },
     * })
     * ```
     */
    File = "file",
    /**
     * A property control that references to another components on the canvas, provided via a property.
     * The component will have an outlet to allow linking to other Frames.
     * Available Frames will also be displayed in a dropdown menu in the
     * properties panel. The component reference will be provided as a property.
     * As a convention, the name for the property is usually just `children`.
     *
     * Multiple components can be linked by combining the `ComponentInstance`
     * type with the {@link ControlType.Array}.
     *
     * ```javascript
     * addPropertyControls(MyComponent, {
     *   children: {
     *     type: ControlType.ComponentInstance,
     *   },
     * })
     * ```
     */
    ComponentInstance = "componentinstance",
    /**
     * A property control that allows multiple values per `ControlType`, provided as an array via properties.
     * For most control types this will be displayed as an additional
     * section in the properties panel allowing as many fields to be provided
     * as required.
     *
     * For a {@link ControlType.ComponentInstance} the Frame will also gain
     * an additional outlet control on the Canvas that allows links to be created between frames.
     *
     * ```javascript
     * addPropertyControls(MyComponent, {
     *   images: {
     *     type: ControlType.Array,
     *     propertyControl: {
     *       type: ControlType.Image
     *     }
     *   },
     *   children: {
     *     type: ControlType.Array,
     *     propertyControl: {
     *       type: ControlType.ComponentInstance
     *     }
     *   },
     * })
     * ```
     */
    Array = "array",
}

const ControlTypeNames = {
    boolean: true,
    number: true,
    string: true,
    fusednumber: true,
    enum: true,
    segmentedenum: true,
    color: true,
    image: true,
    file: true,
    componentinstance: true,
    array: true,
}

function isControlType(name: string): name is ControlType {
    return ControlTypeNames[name]
}

export interface BaseControlDescription<P = any> {
    title?: string
    hidden?(props: P): boolean
}

export interface BooleanControlDescription<P = any> extends BaseControlDescription<P> {
    type: ControlType.Boolean
    defaultValue?: boolean
    disabledTitle?: string
    enabledTitle?: string
}

export interface NumberControlDescription<P = any> extends BaseControlDescription<P> {
    type: ControlType.Number
    defaultValue?: number
    max?: number
    min?: number
    unit?: string
    step?: number
    displayStepper?: boolean
}

export interface StringControlDescription<P = any> extends BaseControlDescription<P> {
    type: ControlType.String
    defaultValue?: string
    placeholder?: string
}

export interface FusedNumberControlDescription<P = any> extends BaseControlDescription<P> {
    type: ControlType.FusedNumber
    defaultValue?: number
    toggleKey: keyof P
    toggleTitles: [string, string]
    valueKeys: [keyof P, keyof P, keyof P, keyof P]
    valueLabels: [string, string, string, string]
    min?: number
}

export interface DeprecatedFusedNumberControlDescription<P = any> extends BaseControlDescription<P> {
    type: ControlType.FusedNumber
    valueKeys: [keyof P, keyof P, keyof P, keyof P]
    valueLabels: [string, string, string, string]
    min?: number
    // deprecated
    splitKey: keyof P
    splitLabels: [string, string]
}

export interface EnumControlDescription<P = any> extends BaseControlDescription<P> {
    type: ControlType.Enum
    defaultValue?: string
    options: string[]
    optionTitles?: string[] | ((props: P | null) => string[])
}

export interface SegmentedEnumControlDescription<P = any> extends BaseControlDescription<P> {
    type: ControlType.SegmentedEnum
    defaultValue?: string
    options: string[]
    optionTitles?: string[] | ((props: P | null) => string[])
}

export interface ColorControlDescription<P = any> extends BaseControlDescription<P> {
    type: ControlType.Color
    defaultValue?: string
}

export interface ImageControlDescription<P = any> extends BaseControlDescription<P> {
    type: ControlType.Image
}

export interface FileControlDescription<P = any> extends BaseControlDescription<P> {
    type: ControlType.File
    allowedFileTypes: string[]
}

export interface ComponentInstanceDescription<P = any> extends BaseControlDescription<P> {
    type: ControlType.ComponentInstance
}

/** @public */
export interface ArrayControlDescription<P = any> extends BaseControlDescription<P> {
    type: ControlType.Array
    propertyControl: FlatControlDescription<P>
    maxCount?: number
    defaultValue?: any[]
}

function toNumber(o: any, min?: number): number | undefined {
    if (typeof o === "number" && (!isFiniteNumber(min) || o >= min)) return o
}

function toString(o: any): string | undefined {
    if (typeof o === "string") return o
}

function toBoolean(o: any): boolean | undefined {
    if (o === true || o === false) return o
}

function toStringList(o: any): string[] | undefined {
    if (o instanceof Array) {
        return o.map(v => toString(v)).filter(v => v) as string[]
    }
}

function toString2(o: any): [string, string] | undefined {
    if (o instanceof Array) {
        const s1 = toString(o[0])
        const s2 = toString(o[1])
        if (s1 && s2) return [s1, s2]
    }
}

function toString4(o: any): [string, string, string, string] | undefined {
    if (o instanceof Array) {
        const s1 = toString(o[0])
        const s2 = toString(o[1])
        const s3 = toString(o[2])
        const s4 = toString(o[3])
        if (s1 && s2 && s3 && s4) return [s1, s2, s3, s4]
    }
}

function toHiddenFunction<P = any>(o: any): ((props: P) => boolean) | undefined {
    if (typeof o === "function") return o
}

function toStringArrayFunction<P = any>(o: any): ((props: P) => string[]) | undefined {
    if (typeof o === "function") return o
}

function toArray(o: any, type: ControlType): any[] | undefined {
    if (!Array.isArray(o)) return
    switch (type) {
        case ControlType.Boolean:
            return o.filter(item => item === true || item === false)
        case ControlType.Number:
            return o.filter(isFiniteNumber)
        case ControlType.String:
        case ControlType.Enum:
        case ControlType.SegmentedEnum:
        case ControlType.Color:
            return toStringList(o)
    }
}

function defaultControl(
    type: ControlType,
    values: Partial<
        BooleanControlDescription &
            NumberControlDescription &
            StringControlDescription &
            FusedNumberControlDescription &
            DeprecatedFusedNumberControlDescription &
            EnumControlDescription &
            SegmentedEnumControlDescription &
            ColorControlDescription &
            ImageControlDescription &
            FileControlDescription &
            ComponentInstanceDescription &
            ArrayControlDescription
    >
): ControlDescription | ArrayControlDescription | null {
    const title = toString(values.title)
    const hidden = toHiddenFunction(values.hidden)
    const defaultValue = values.defaultValue
    switch (type) {
        case ControlType.Boolean: {
            const disabledTitle = toString(values.disabledTitle)
            const enabledTitle = toString(values.enabledTitle)
            return { title, hidden, defaultValue: toBoolean(defaultValue), type, disabledTitle, enabledTitle }
        }
        case ControlType.Number: {
            const min = toNumber(values.min)
            const max = toNumber(values.max)
            const unit = toString(values.unit)
            const step = toNumber(values.step)
            const displayStepper = toBoolean(values.displayStepper)
            return { title, hidden, defaultValue: toNumber(defaultValue), type, min, max, unit, step, displayStepper }
        }
        case ControlType.String:
            const placeholder = toString(values.placeholder)
            return { title, hidden, defaultValue, type, placeholder }
        case ControlType.FusedNumber: {
            const valueKeys = toString4(values.valueKeys)
            const valueLabels = toString4(values.valueLabels)
            const min = toNumber(values.min)

            let toggleKey = toString(values.toggleKey)
            let toggleTitles = toString2(values.toggleTitles)

            if (!toggleKey) {
                toggleKey = toString(values.splitKey)
                if (!toggleKey) return null
                deprecationWarning("splitKey option of FusedNumber control type", "1.0.0", "toggleKey")
            }

            if (!toggleTitles) {
                toggleTitles = toString2(values.splitLabels)
                if (!toggleTitles) return null
                deprecationWarning("splitLabels option of FusedNumber control type", "1.0.0", "toggleTitles")
            }

            if (!toggleKey || !toggleTitles || !valueKeys || !valueLabels) return null
            return {
                title,
                hidden,
                defaultValue: toNumber(defaultValue),
                type,
                toggleKey,
                toggleTitles,
                valueKeys,
                valueLabels,
                min,
            }
        }
        case ControlType.Enum: {
            const options = toStringList(values.options)
            if (!options) return null
            const optionTitles = toStringList(values.optionTitles) || toStringArrayFunction(values.optionTitles)
            return { title, hidden, defaultValue: toString(defaultValue), type, options, optionTitles }
        }
        case ControlType.SegmentedEnum: {
            const options = toStringList(values.options)
            if (!options) return null
            const optionTitles = toStringList(values.optionTitles) || toStringArrayFunction(values.optionTitles)
            return { title, hidden, defaultValue: toString(defaultValue), type, options, optionTitles }
        }
        case ControlType.Color:
            return { title, hidden, defaultValue: toString(defaultValue), type }
        case ControlType.Image:
            return { title, hidden, type }
        case ControlType.File:
            const allowedFileTypes = toStringList(values.allowedFileTypes)
            if (!allowedFileTypes) return null
            return { title, hidden, type, allowedFileTypes }
        case ControlType.ComponentInstance:
            return { title, hidden, type }
        case ControlType.Array:
            if (!values.propertyControl || !values.propertyControl.type) return null
            const propertyControl = defaultControl(values.propertyControl.type, values.propertyControl as object) // No idea why this cast is needed
            if (!propertyControl) return null
            if (propertyControl.type === ControlType.Array) return null
            const maxCount = toNumber(values.maxCount, 1)
            return {
                title,
                hidden,
                type,
                propertyControl,
                maxCount,
                defaultValue: toArray(values.defaultValue, propertyControl.type),
            }
    }
    return null
}

export function verifyPropertyControls(desc: object): PropertyControls<any> {
    const controls: PropertyControls<any> = {}
    for (const key in desc) {
        const value = desc[key]
        if (typeof value === "string" && isControlType(value)) {
            const d = defaultControl(value, {})
            if (d) {
                controls[key] = d
            }
            continue
        }
        const type = value.type
        if (typeof type === "string" && isControlType(type)) {
            const d = defaultControl(type, value)
            if (d) {
                controls[key] = d
            }
            continue
        }
    }

    return controls
}
